/**
 * workflowSessionStore.ts — Workflow 页 zustand store
 *
 * 职责：
 *     Source 事件写入 WorkflowSessionState；与 SessionStore 平行隔离。
 *
 * 链路位置：
 *     WorkflowSession Provider → applyEvent。
 *
 * 当前裁剪范围：
 *     无多会话 Tab；dedupe 与 SessionStore 同构。
 */
import { createStore, type StoreApi } from 'zustand/vanilla';

import type { LangchainAgentEvent, TierOverrides } from '../types/events';
import type { ReducerError, SessionTree } from '../types/tree';
import {
  createEmptyWorkflowSessionState,
  type WorkflowSessionState,
} from '../types/workflowSession';
import { markEventIdSeen, shouldSkipDuplicateEvent } from './dedupe';
import { createEmptyTree, type ReduceOptions, reduceTree } from './reducer';
import {
  finalizeWorkflowSessionOnStreamComplete,
  reduceWorkflowSession,
} from './workflowReducer';
import {
  evictInactiveLoopTrees,
  evictCompletedLoopOverflow,
  touchCompletedLoopSession,
} from './workflow/workflowLoopEviction';
import { filterAgentLoopReplayEvents } from './workflowStructureEvents';
import type { SourceEventContext } from '../transport/types';
import { finalizeWorkflowLoopTree } from './workflow/workflowLoopTreeUtils';
import type { WorkflowContainerRuntimeNode } from '../types/workflowContainer';

export interface WorkflowSessionStoreOptions {
  tierOverrides?: TierOverrides;
  initialEventCount?: number;
  initialSeenEventIds?: Iterable<string>;
  /** P2：completed loop 树内存淘汰（默认 true） */
  evictCompletedLoops?: boolean;
  /** P2：completed hydrated loop 热缓存上限（默认 4） */
  maxHydratedCompletedLoops?: number;
}

export interface WorkflowSessionStoreState {
  state: WorkflowSessionState;
  eventCount: number;
  /** DES-1：分批 hydrate 进行中的 loopSessionId（store 级，不进 reducer） */
  hydratingLoopSessionIds: Record<string, true>;
  applyEvent: (event: LangchainAgentEvent, ctx?: SourceEventContext) => void;
  applyEvents: (events: LangchainAgentEvent[]) => void;
  reset: () => void;
  markAsError: (error?: Error) => void;
  /** SSE 正常结束：推断 done 并收口 running loop（嵌套 parallel 缺 workflow-end 兜底） */
  markStreamComplete: () => void;
  /** P2：展开 done stage 时 pin，避免淘汰 */
  pinLoopSession: (loopSessionId: string) => void;
  /** P2：宿主 replay 后灌入 loop 树（同步；测试与小批量） */
  hydrateLoopSession: (loopSessionId: string, events: LangchainAgentEvent[]) => void;
  /** DES-1：宿主默认 replay 路径 — 分批 + rAF yield */
  hydrateLoopSessionAsync: (
    loopSessionId: string,
    events: LangchainAgentEvent[],
    options?: HydrateLoopSessionAsyncOptions,
  ) => Promise<void>;
}

export interface HydrateLoopSessionAsyncOptions {
  chunkSize?: number;
  signal?: AbortSignal;
}

const DEFAULT_HYDRATE_CHUNK_SIZE = 50;

function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function withoutHydrating(
  ids: Record<string, true>,
  loopSessionId: string,
): Record<string, true> {
  if (!ids[loopSessionId]) return ids;
  const next = { ...ids };
  delete next[loopSessionId];
  return next;
}

function safeReduceWorkflow(
  state: WorkflowSessionState,
  event: LangchainAgentEvent,
  eventIndex: number,
  options?: ReduceOptions,
): { state: WorkflowSessionState; error?: ReducerError } {
  try {
    return { state: reduceWorkflowSession(state, event, eventIndex, options) };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;
    if (import.meta.env?.DEV) {
      console.error('[langchain_agentx_stream_ui] workflow reducer error:', err);
    }
    return {
      state: {
        ...state,
        internalErrors: [
          ...state.internalErrors,
          { eventIndex, eventType: event.event_type, message, stack },
        ],
      },
    };
  }
}

/** scope miss / 传输断流：收口 running 容器与 loop，避免永久 Spinner（D30 A-03）。 */
function finalizeWorkflowSessionOnStreamError(
  state: WorkflowSessionState,
  message: string,
  eventIndex: number,
  stack?: string,
): WorkflowSessionState {
  const containersById: Record<string, WorkflowContainerRuntimeNode> = {
    ...state.containerTree.containersById,
  };
  for (const [id, node] of Object.entries(containersById)) {
    if (node.status === 'running' || node.status === 'pending') {
      containersById[id] = { ...node, status: 'failed', is_open: false };
    }
  }

  const loopTreesBySessionId = { ...state.loopTreesBySessionId };
  for (const [loopSessionId, tree] of Object.entries(loopTreesBySessionId)) {
    if (tree.status === 'running' || tree.status === 'connecting') {
      loopTreesBySessionId[loopSessionId] = finalizeWorkflowLoopTree(tree, 'error');
    }
  }

  return {
    ...state,
    status: 'error',
    containerTree: {
      ...state.containerTree,
      containersById,
      activeContainerIds: state.containerTree.activeContainerIds.filter(
        (id) => containersById[id]?.is_open,
      ),
    },
    loopTreesBySessionId,
    activeLoopSessionId: null,
    internalErrors: [
      ...state.internalErrors,
      { eventIndex, eventType: 'stream-error', message, stack },
    ],
  };
}

export type WorkflowSessionStore = StoreApi<WorkflowSessionStoreState>;

export function createWorkflowSessionStore(
  initialState: WorkflowSessionState = createEmptyWorkflowSessionState(),
  storeOptions?: WorkflowSessionStoreOptions,
): WorkflowSessionStore {
  const reduceOpts: ReduceOptions = {
    tierOverrides: storeOptions?.tierOverrides,
  };
  const initialEventCount = storeOptions?.initialEventCount ?? 0;
  const seenEventIds = new Set<string>(storeOptions?.initialSeenEventIds);
  const evictCompletedLoops = storeOptions?.evictCompletedLoops ?? true;
  const maxHydratedCompletedLoops = storeOptions?.maxHydratedCompletedLoops ?? 4;
  const pinnedLoopSessionIds: Record<string, true> = {};

  const finalizeState = (reduced: WorkflowSessionState): WorkflowSessionState => {
    if (!evictCompletedLoops) return reduced;
    return evictInactiveLoopTrees(reduced, pinnedLoopSessionIds);
  };

  return createStore<WorkflowSessionStoreState>((set, get) => ({
    state: initialState,
    eventCount: initialEventCount,
    hydratingLoopSessionIds: {},
    applyEvent(event, ctx) {
      const sseEventId = ctx?.sseEventId;
      if (shouldSkipDuplicateEvent(seenEventIds, sseEventId)) return;

      const { state, eventCount } = get();
      const { state: reduced } = safeReduceWorkflow(state, event, eventCount, reduceOpts);
      const evicted = finalizeState(reduced);
      const nextMeta =
        sseEventId
          ? { ...evicted.meta, lastEventId: sseEventId }
          : evicted.meta;
      markEventIdSeen(seenEventIds, sseEventId);
      set({
        state: {
          ...evicted,
          meta: nextMeta,
          status: evicted.status === 'connecting' ? 'running' : evicted.status,
        },
        eventCount: eventCount + 1,
      });
    },
    applyEvents(events) {
      for (const event of events) {
        get().applyEvent(event);
      }
    },
    reset() {
      seenEventIds.clear();
      set({
        state: createEmptyWorkflowSessionState(),
        eventCount: 0,
        hydratingLoopSessionIds: {},
      });
    },
    markAsError(error) {
      const { state, eventCount } = get();
      const message = error?.message ?? 'Workflow stream error';
      set({
        state: finalizeWorkflowSessionOnStreamError(
          state,
          message,
          eventCount,
          error?.stack,
        ),
      });
    },
    markStreamComplete() {
      set((current) => ({
        state: finalizeState(
          finalizeWorkflowSessionOnStreamComplete(current.state),
        ),
      }));
    },
    /**
     * P2：将 completed loop 加入 LRU 热缓存并淘汰溢出。
     * running 保护由 collectProtectedLoopSessionIds 按状态自动覆盖，不再依赖永久 pin。
     */
    pinLoopSession(loopSessionId) {
      set((current) => {
        let next = touchCompletedLoopSession(current.state, loopSessionId);
        next = evictCompletedLoopOverflow(next, maxHydratedCompletedLoops);
        next = finalizeState(next);
        return { state: next };
      });
    },
    /**
     * P2：宿主 replay 后灌入 loop 树，touch LRU 并淘汰溢出。
     * completed loop 受 hotCompletedLoopSessionIds 保护，不再永久 pin。
     */
    hydrateLoopSession(loopSessionId, events) {
      const { state, eventCount } = get();
      let tree: SessionTree =
        state.loopTreesBySessionId[loopSessionId] ?? createEmptyTree();
      let idx = eventCount;
      for (const event of filterAgentLoopReplayEvents(events)) {
        tree = reduceTree(tree, event, idx, reduceOpts);
        idx += 1;
      }
      set((current) => {
        let next: WorkflowSessionState = {
          ...current.state,
          loopTreesBySessionId: {
            ...current.state.loopTreesBySessionId,
            [loopSessionId]: tree,
          },
        };
        next = touchCompletedLoopSession(next, loopSessionId);
        next = evictCompletedLoopOverflow(next, maxHydratedCompletedLoops);
        next = finalizeState(next);
        return { state: next };
      });
    },
    async hydrateLoopSessionAsync(loopSessionId, events, options) {
      const { hydratingLoopSessionIds } = get();
      if (hydratingLoopSessionIds[loopSessionId]) return;

      const filtered = filterAgentLoopReplayEvents(events);
      if (filtered.length === 0) return;

      const chunkSize = options?.chunkSize ?? DEFAULT_HYDRATE_CHUNK_SIZE;
      const signal = options?.signal;

      set((current) => ({
        hydratingLoopSessionIds: {
          ...current.hydratingLoopSessionIds,
          [loopSessionId]: true,
        },
      }));

      try {
        let tree: SessionTree =
          get().state.loopTreesBySessionId[loopSessionId] ?? createEmptyTree();
        let idx = get().eventCount;

        for (let offset = 0; offset < filtered.length; offset += chunkSize) {
          if (signal?.aborted) {
            throw new DOMException('Hydrate aborted', 'AbortError');
          }

          const chunk = filtered.slice(offset, offset + chunkSize);
          for (const event of chunk) {
            tree = reduceTree(tree, event, idx, reduceOpts);
            idx += 1;
          }

          const isLastChunk = offset + chunkSize >= filtered.length;
          set((current) => {
            let next: WorkflowSessionState = {
              ...current.state,
              loopTreesBySessionId: {
                ...current.state.loopTreesBySessionId,
                [loopSessionId]: tree,
              },
            };
            if (isLastChunk) {
              next = touchCompletedLoopSession(next, loopSessionId);
              next = evictCompletedLoopOverflow(next, maxHydratedCompletedLoops);
            }
            next = finalizeState(next);
            return { state: next };
          });

          if (!isLastChunk) {
            await nextAnimationFrame();
          }
        }
      } catch {
        set((current) => {
          const { [loopSessionId]: _removed, ...restLoops } =
            current.state.loopTreesBySessionId;
          return {
            state: { ...current.state, loopTreesBySessionId: restLoops },
            hydratingLoopSessionIds: withoutHydrating(
              current.hydratingLoopSessionIds,
              loopSessionId,
            ),
          };
        });
        return;
      }

      set((current) => ({
        hydratingLoopSessionIds: withoutHydrating(
          current.hydratingLoopSessionIds,
          loopSessionId,
        ),
      }));
    },
  }));
}
